import { auth } from "@fynd-onshelf/contracts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import { z } from "zod";

import { AuthShell } from "../components/auth/AuthShell.js";
import { PasswordInput } from "../components/auth/PasswordInput.js";
import { Button } from "../components/ui/button.js";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form.js";
import { Input } from "../components/ui/input.js";
import { useSignupStartMutation } from "../lib/api/auth.js";
import { useInvitationLookupQuery } from "../lib/api/invitations.js";
import {
  authDestination,
  clearAuthReturnTo,
  invitationTokenFromReturnTo,
  rememberAuthReturnTo,
  safeAuthReturnTo,
} from "../lib/auth/return-to.js";
import { APIError } from "../lib/http.js";
import { useAuth } from "../providers/auth-provider.js";

const signupSchema = z.object({
  firstName: z.string().min(1, "Required").max(255),
  lastName: z.string().min(1, "Required").max(255),
  email: z.string().email("Enter a valid email").max(255),
  // Shared with the API contract (min 8 / max 256) — single source of truth.
  password: auth.passwordSchema,
});
type SignupValues = z.infer<typeof signupSchema>;

export function SignupPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const returnTo = safeAuthReturnTo(searchParams.get("returnTo"));
  const invitation = useInvitationLookupQuery(invitationTokenFromReturnTo(returnTo) ?? null);
  // Initialize the form with the lookup result so prefill cannot race typing.
  if (invitation.isLoading && !invitation.isFetched)
    return <div className="p-8 text-sm text-muted-foreground">Loading invitation…</div>;
  return (
    <SignupForm
      key={returnTo}
      returnTo={returnTo}
      invitedEmail={invitation.data?.invitedEmail ?? ""}
    />
  );
}

function SignupForm({
  returnTo,
  invitedEmail,
}: {
  returnTo: string | null;
  invitedEmail: string;
}): JSX.Element {
  const { user, loading } = useAuth();
  const signupMut = useSignupStartMutation();
  const loginLinkTarget = returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : "/login";
  const inviteToken = invitationTokenFromReturnTo(returnTo);

  useEffect(() => {
    if (signupMut.data?.verified || (!loading && user)) {
      clearAuthReturnTo();
      window.location.replace(
        authDestination(returnTo, signupMut.data?.user?.tenantCode ?? user?.tenantCode),
      );
    }
  }, [signupMut.data, returnTo, loading, user]);

  const form = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { firstName: "", lastName: "", email: invitedEmail, password: "" },
    mode: "onSubmit",
  });

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (user || signupMut.data?.verified)
    return <div className="p-8 text-sm text-muted-foreground">Redirecting…</div>;

  const submitError = signupMut.error
    ? signupMut.error instanceof APIError
      ? signupMut.error.displayDetail
      : (signupMut.error as Error).message
    : null;
  // "Check your email" screen is only for the normal (email-verification) flow.
  // The auto-verified invited flow redirects via the effect above, so exclude it.
  const success = signupMut.data && !signupMut.data.verified ? signupMut.data : undefined;

  return (
    <AuthShell
      title={success ? "Check your email" : "Create your account"}
      subtitle={
        success
          ? "Verify your email to finish setting up your Onshelf workspace"
          : "Start your Onshelf workspace"
      }
    >
      {success ? (
        <div className="flex flex-col gap-4">
          <p className="rounded-md border border-border bg-muted/50 p-3 text-center text-sm text-foreground">
            We sent a verification link to <strong>{form.getValues("email")}</strong>.
          </p>
          {success.devLink ? (
            <p className="rounded-md border border-dashed border-border bg-card p-3 text-xs">
              <span className="font-medium">Dev:</span>{" "}
              <a href={success.devLink} className="break-all text-primary hover:underline">
                {success.devLink}
              </a>
            </p>
          ) : null}
          <Link
            to={loginLinkTarget}
            className="block text-center text-sm font-medium text-primary hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => {
              // Stash returnTo BEFORE the mutation fires; the user will leave
              // this page (via email link) before any react state matters.
              rememberAuthReturnTo(returnTo, values.email);
              signupMut.mutate({ ...values, invitationToken: inviteToken });
            })}
            className="flex flex-col gap-4"
            noValidate
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="given-name"
                        autoFocus
                        placeholder="Enter first name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input autoComplete="family-name" placeholder="Enter last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email ID</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="Enter Email ID"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete="new-password"
                      placeholder="Enter Password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {submitError ? (
              <p className="text-sm text-destructive" role="alert">
                {submitError}
              </p>
            ) : null}
            <Button
              type="submit"
              size="lg"
              className="mt-1 w-full rounded-full"
              disabled={signupMut.isPending}
            >
              {signupMut.isPending ? "Creating account…" : "Create account"}
            </Button>
            <div className="mt-1 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to={loginLinkTarget} className="font-medium text-primary hover:underline">
                Sign in
              </Link>
            </div>
          </form>
        </Form>
      )}
    </AuthShell>
  );
}
